import { CabecalhoDePagina, EstadoCarregando, EstadoDeErro, cn } from "@base/ui";
import { ArrowLeft, Database, ListFilter, PlugZap, Workflow } from "lucide-react";
import { useState, type ReactNode } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link, parametroDaUrl } from "../../../shared/navegacao/navegacao";
import { useConexaoKommo } from "../application/conexoes.contexto";
import { CamposDoFiltro } from "./campos-do-filtro";
import { RecorteDoProjeto } from "./recorte-do-projeto";
import { MapeamentoDoFunil } from "./mapeamento-do-funil";
import { AbaDaConta } from "./aba-da-conta";
import { FormularioDeConexao } from "./painel-do-kommo";

export const CAMINHO_DO_KOMMO = "/configuracoes/conexoes/kommo";

const ABAS = [
  { id: "conta", rotulo: "Conta", icone: Database },
  { id: "funil", rotulo: "Etapas do funil", icone: Workflow },
  { id: "filtros", rotulo: "Filtros do Dashboard", icone: ListFilter },
] as const;
type Aba = (typeof ABAS)[number]["id"];

const abaDaUrl = (): Aba => {
  const aba = parametroDaUrl("aba");
  return ABAS.some((item) => item.id === aba) ? (aba as Aba) : "conta";
};

/**
 * Configuracoes do Kommo CRM de um cliente, numa tela so: a conta conectada,
 * a etapa do Kommo de cada item do funil e os campos que filtram o Dashboard.
 * A aba fica na URL (`?aba=funil`), para dar para mandar o link direto.
 */
export function PaginaDoKommo({ clienteId, cliente }: { readonly clienteId: string; readonly cliente: string }) {
  const kommo = useConexaoKommo(clienteId);
  const [aba, setAba] = useState<Aba>(abaDaUrl);

  function trocarAba(proxima: Aba) {
    setAba(proxima);
    window.history.replaceState(null, "", proxima === "conta" ? CAMINHO_DO_KOMMO : `${CAMINHO_DO_KOMMO}?aba=${proxima}`);
  }

  const cabecalho = () => (
    <div className="flex flex-col gap-4">
      <Link
        para="/configuracoes/conexoes"
        className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Conexões
      </Link>
      <CabecalhoDePagina
        rotulo={`${cliente} · Conexões`}
        titulo="Kommo CRM"
        descricao="Tudo da integração com o CRM desta clínica: conta, etapas do funil e filtros do Dashboard."
      />
    </div>
  );

  if (kommo.isPending) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho()}
        <EstadoCarregando rotulo="Carregando a conexão com o Kommo" />
      </div>
    );
  }
  if (kommo.isError) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho()}
        <EstadoDeErro mensagem={mensagemDoErro(kommo.error)} aoTentarNovamente={() => void kommo.refetch()} />
      </div>
    );
  }

  const conexao = kommo.data;

  if (conexao.situacao === "nao-conectada") {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho()}
        <Cartao titulo="Conectar o Kommo" icone={<PlugZap className="size-5" aria-hidden="true" />}>
          <FormularioDeConexao clienteId={clienteId} subdominioAtual={null} aoConcluir={() => trocarAba("funil")} />
        </Cartao>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-secao">
      {cabecalho()}

      <nav aria-label="Configurações do Kommo" className="-mt-4 flex gap-1 overflow-x-auto border-b [scrollbar-width:none]">
        {ABAS.map(({ id, rotulo, icone: Icone }) => (
          <button
            key={id}
            type="button"
            aria-current={aba === id ? "page" : undefined}
            onClick={() => trocarAba(id)}
            className={cn(
              "-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
              aba === id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icone className="size-4" aria-hidden="true" />
            {rotulo}
            {id === "funil" && !conexao.mapeamento ? (
              <span className="size-2 rounded-full bg-warning" aria-label="pendente" />
            ) : null}
          </button>
        ))}
      </nav>

      {aba === "conta" ? (
        <AbaDaConta clienteId={clienteId} conexao={conexao} />
      ) : aba === "funil" ? (
        <MapeamentoDoFunil
          key={`${conexao.subdominio}:${JSON.stringify(conexao.mapeamento)}:${conexao.pipelines.length}`}
          clienteId={clienteId}
          conexao={conexao}
        />
      ) : (
        <div className="flex flex-col gap-secao">
          <RecorteDoProjeto
            key={`${conexao.subdominio}:${JSON.stringify(conexao.recorte)}`}
            clienteId={clienteId}
            conexao={conexao}
          />
          <CamposDoFiltro
            key={`${conexao.subdominio}:${conexao.camposDoFiltro.join(",")}:${conexao.camposPersonalizados.length}`}
            clienteId={clienteId}
            conexao={conexao}
          />
        </div>
      )}
    </div>
  );
}

function Cartao({ titulo, icone, children }: { readonly titulo: string; readonly icone: ReactNode; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-6 rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70 md:p-8">
      <h2 className="flex items-center gap-3 text-lg font-medium">
        <span className="grid size-10 place-items-center rounded-2xl bg-accent text-accent-foreground">{icone}</span>
        {titulo}
      </h2>
      {children}
    </section>
  );
}
